import { Component, computed, inject, input } from '@angular/core';
import { DiceRoll } from '@dice-roller/rpg-dice-roller';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonNote,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import {
  ChaosFragment,
  findByName,
  forEachFragments,
  iterateAllInsert,
  iterateAllReferenceToSheet,
  parseModifiers,
  ReferenceToSheetFragment,
  ValueFragment,
} from 'operationlib';
import { parseOperation } from 'operationlib';
import { OperationRenderComponent } from '../operation-render/operation-render.component';
import { TRANSLATE_IMPORTS } from 'src/app/features/i18n/translate-imports';

type RollInfo = {
  type: string;
  name: string | undefined;
  roll: DiceRoll;
};

@Component({
  selector: 're-operation-detail',
  imports: [
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonChip,
    IonContent,
    IonHeader,
    IonIcon,
    IonNote,
    IonTitle,
    IonToolbar,
    OperationRenderComponent,
    ...TRANSLATE_IMPORTS,
  ],
  templateUrl: './operation-detail.component.html',
  styleUrls: ['./operation-detail.component.scss'],
})
export class OperationDetailComponent {
  private modalCtrl = inject(ModalController);

  name = input<string>();
  character = input<string | undefined>();
  original = input<string>();
  statSelected = input<string>();
  statCompiled = input<string>();
  rolled = input<string>();
  final = input<string>();
  result = input<string>();

  resultValue = computed(() => +(this.result() ?? '0'));

  /** Allineato a operationlib: esito boolean positivo se il valore è > 0 (es. 1/0). */
  booleanSuccess = computed(() => this.resultValue() > 0);

  modifiers = computed(() =>
    this.original() ? parseModifiers(this.original()?.split(';')[1]!) : []
  );

  resultType = computed(() => {
    return (
      this.modifiers().find((el) => el.type === 'result')?.value ?? 'value'
    );
  });

  rolledParsed = computed(() =>
    this.rolled() ? parseOperation(this.rolled()!) : []
  );
  chaosFragments = computed(() => {
    let list: ChaosFragment[] = [];
    forEachFragments(this.rolledParsed(), (fragment) => {
      if (fragment.type === 'chaos') {
        list.push(fragment);
      }
    });
    return list;
  });
  allChaosResult = computed(() => {
    return this.chaosFragments()
      .map((fr) => {
        let openIndex = fr.value.indexOf('[');
        let closeIndex = fr.value.lastIndexOf(']');
        let content = fr.value.slice(openIndex + 1, closeIndex);
        let chaosType = fr.value.slice(closeIndex + 1).split('#')[0];
        return {
          type: chaosType,
          name: fr.name,
          roll: DiceRoll.import(content),
        } as RollInfo;
      })
      .reduce(
        (acc, curr) => {
          if (curr.name) {
            acc.named.push(curr);
          } else {
            acc.unNamed.push(curr);
          }
          return acc;
        },
        {
          named: [] as RollInfo[],
          unNamed: [] as RollInfo[],
        }
      );
  });

  originalParsed = computed(() =>
    this.original() ? parseOperation(this.original()!) : []
  );
  finalParsed = computed(() =>
    this.final() ? parseOperation(this.final()!) : []
  );

  allInserted = computed(() => {
    let list: { name: string; value: number }[] = [];
    iterateAllInsert(this.originalParsed(), 'execution', (el) => {
      if (el.name) {
        let res = findByName<ValueFragment>(this.finalParsed(), el.name);
        list.push({
          name: el.name,
          value: res ? +res.value : 0,
        });
      }
    });
    return list;
  });
  statSelectedParsed = computed(() =>
    this.statSelected() ? parseOperation(this.statSelected()!) : []
  );

  allStatSelected = computed(() => {
    let list: {
      name: string;
      selected: string[];
      unselected: string[];
      result: string;
    }[] = [];
    iterateAllReferenceToSheet(this.originalParsed(), (el) => {
      if (el.name) {
        let res = findByName<ReferenceToSheetFragment>(
          this.statSelectedParsed(),
          el.name
        );
        let result = findByName<ReferenceToSheetFragment>(
          this.finalParsed(),
          el.name
        );
        list.push({
          name: el.name,
          unselected: el.value
            .slice(1, -1)
            .replaceAll(' ', '')
            .split('->')
            .map((el) => (el.includes('@') || el.includes('*') ? '*' : el))
            .map((el) => el.split('#')[0]),
          selected: res.value
            .slice(1, -1)
            .replaceAll(' ', '')
            .split('->')
            .map((el) => el.split('#')[0]),
          result: result.value,
        });
      }
    });
    return list;
  });

  dismiss(): void {
    this.modalCtrl.dismiss();
  }
}
